import useDeleteChatbot from '@/hooks/useDeleteChatbot';
import { useMakeFolderPrivate } from '@/hooks/useMakeFolderPrivate';
import { useMakeFolderTeam } from '@/hooks/useMakeFolderTeam';
import { useRenameGroup } from '@/hooks/useRenameGroup';
import { useSeparateGroup } from '@/hooks/useSeparateGroup';
import { useUpdateName } from '@/hooks/useUpdateName';
import { useTeamId } from '@/hooks/useWorkspace';
import type { TreeItem } from '@/lib/tree/types';
import { cn } from '@/utils/utils';
import {
  EllipsisVertical,
  LockIcon,
  SplitIcon,
  SquarePen,
  TrashIcon,
  Users,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '../Button/Button';
import { useConfirmationStore } from '../Confirmation/ConfirmationStore';
import { useSelectedItems } from '../Menus/SelectMenu';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/DropdownMenu';
import { Icon } from '../ui/Icon';
import RenameForm from './RenameForm';

interface SidebarDropdownProps {
  tree: TreeItem;
  isActive?: boolean;
  onSidebarOpenChange?: (open: boolean) => void;
}
const SidebarDropdown = ({
  tree,
  isActive,
  onSidebarOpenChange,
}: SidebarDropdownProps) => {
  const [openRename, setOpenRename] = useState(false);
  const [openMenu, setOpenMenu] = useState(false);
  const { setSelectedItems, selectedItems } = useSelectedItems();
  const { groupIdToRename, setGroupIdToRename } = useRenameGroup();

  const { mutateDelete } = useDeleteChatbot({
    onMutate: () => {
      setOpenMenu(false);
    },
  });

  const { mutateAsync: makeFolderPrivate } = useMakeFolderPrivate();

  const { mutateAsync: makeFolderTeam } = useMakeFolderTeam();

  const teamId = useTeamId();

  const { mutateAsync: separateMutate } = useSeparateGroup();

  const { updateName, isRenaming } = useUpdateName();
  const confirm = useConfirmationStore((state) => state.openConfirmation);

  const name = tree.name;

  useEffect(() => {
    if (groupIdToRename !== tree.id) return;
    const timeout = setTimeout(() => {
      setOpenMenu(true);
      setOpenRename(true);
      setGroupIdToRename(null);
    }, 100);

    return () => clearTimeout(timeout);
  }, [groupIdToRename, tree.id, setGroupIdToRename]);

  if (tree.type === 'WORKSPACE') return null;

  const isGroup = tree.type === 'GROUP';

  const isLoading = isRenaming;
  const handleDeleteClick = () => {
    setOpenMenu(false);

    const selectedItemsIds = selectedItems?.map((item) => ({
      id: item.id as string,
    }));
    const isSelectedItem = selectedItemsIds?.find(
      (item) => item.id === tree.id,
    );
    const idsToDelete = isSelectedItem
      ? selectedItemsIds
      : [{ id: tree.id as string }];

    if (isGroup) {
      const ids = tree.children.map((child) => ({ id: child.id as string }));
      idsToDelete.push(...ids);
    }

    confirm({
      title: 'Delete folder?',
      description:
        'Files and chats inside this folder will be permanently  deleted. Nested subfolders will not be deleted. ',
      actionLabel: 'Delete folder',
      actionButtonVariant: 'destructive',
      onAction: () => {
        mutateDelete(idsToDelete);
        if (isSelectedItem) {
          setSelectedItems([]);
        }
      },
      cancelLabel: 'Cancel',
      onCancel: () => {},
    });
  };

  const isTeam = !!tree.teamId;

  return (
    <DropdownMenu
      open={openMenu}
      onOpenChange={(open) => {
        setOpenMenu(open);
        onSidebarOpenChange?.(open);

        setTimeout(() => {
          setOpenRename(false);
        }, 100);
      }}
    >
      <DropdownMenuTrigger disabled={isLoading} asChild>
        <Button
          isLoading={isLoading}
          size="icon"
          variant="ghost"
          className={cn(
            'absolute top-1/2 right-0.5 -translate-y-1/2 z-20 hover:bg-secondary size-6 focus:outline-none',
            !isLoading &&
              !isTeam &&
              'invisible group-hover:visible data-[state=open]:visible',
            isActive && 'visible',
          )}
        >
          <Icon
            as={
              openMenu
                ? EllipsisVertical
                : isTeam && !isActive
                  ? Users
                  : EllipsisVertical
            }
          />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="min-w-[160px]" align="end">
        {openRename && (
          <RenameForm
            defaultValues={{ name: name ?? tree.name }}
            updateName={(name: string) => {
              updateName({
                id: tree.id as string,
                name: name,
                type: 'CHATBOT',
              });
            }}
            onSubmit={() => {
              setOpenMenu(false);
            }}
          />
        )}

        {!openRename && (
          <>
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                setOpenRename(true);
              }}
            >
              <Icon size="sm" as={SquarePen} />
              Rename
            </DropdownMenuItem>

            {isGroup && (
              <DropdownMenuItem
                onSelect={() => {
                  separateMutate(tree.id as string);
                }}
              >
                <Icon size="sm" as={SplitIcon} />
                Separate
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onSelect={handleDeleteClick}>
              <Icon size="sm" as={TrashIcon} />
              Delete
            </DropdownMenuItem>

            {tree.teamId && (
              <DropdownMenuItem
                onSelect={() => {
                  setOpenMenu(false);
                  confirm({
                    title: 'Make this folder private?',
                    description:
                      'Only you will be able to access and edit the contents of this folder and all nested subfolders.',
                    actionLabel: 'Confirm',
                    onAction: () => {
                      makeFolderPrivate({ id: tree.id as string });
                    },
                    cancelLabel: 'Cancel',
                    onCancel: () => {},
                  });
                }}
              >
                <Icon size="sm" as={LockIcon} />
                Make private
              </DropdownMenuItem>
            )}

            {!tree.teamId && teamId && (
              <DropdownMenuItem
                onSelect={() => {
                  setOpenMenu(false);
                  confirm({
                    title: 'Share folder with team?',
                    description:
                      'Anyone in your team will be able to access and edit the contents of this folder and all nested subfolders.',
                    actionLabel: 'Confirm',
                    onAction: () => {
                      makeFolderTeam({
                        id: tree.id as string,
                        teamId,
                      });
                    },
                    cancelLabel: 'Cancel',
                    onCancel: () => {},
                  });
                }}
              >
                <Icon size="sm" as={Users} />
                Share with team
              </DropdownMenuItem>
            )}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default SidebarDropdown;
